import React, {useImperativeHandle, forwardRef, useState} from 'react';
import {
  Box,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  ExpressCheckoutElement,
} from '@stripe/react-stripe-js';
import {
  StripeElementChangeEvent,
  PaymentMethod,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import MasterCardIcon from '$assets/svgs/BackgroundMasterCard.svg?react';
import Loading from '$components/Loading';

import {FaCreditCard} from 'react-icons/fa';
import AppleIcon from '$assets/svgs/ApplePay.svg?react';
import PayPal from '$assets/svgs/PayPal.svg?react';
import PayPalLogo from '$assets/svgs/paypal-logo.svg?react';

import AmexIcon from '$assets/svgs/Amex-Card.svg?react';
import VisaCardIcon from '$assets/svgs/Visa card.svg?react';
import {
  selectedPaymentOptionStyle,
  cardFormContainerStyle,
  mainContainerStyle,
  paymentOptionsContainerStyle,
  cardElementOptions,
  cardLabelStyle,
  paymentMethodLabelStyle,
  cardWidgetContainerStyle,
  loadingContainerStyle,
  creditCardIconStyle,
  cardFormColumnStyle,
  cardNumberRowStyle,
  cardNumberColumnStyle,
  cardIconsContainerStyle,
  cardDetailsRowStyle,
  cardExpiryColumnStyle,
  cardCvcRowStyle,
  cardCvcColumnStyle,
  cardCvcIconContainerStyle,
  countryColumnStyle,
  inputLabelStyle,
  smallCreditCardIconStyle,
  errorMessageStyle,
  selectedCountryLabelStyle,
  countryArrowIconStyle,
  countrySelectRowStyle,
  countrySelectOverlayStyle,
} from './style';
import {useCountries} from '$modules/booking/hooks';
import {KeyboardArrowDown} from '@mui/icons-material';

const PAYMENT_CONFIG = {
  errors: {
    stripeNotInitialized: 'Stripe not initialized',
    cardDetailsNotFound: 'Card details not found',
    failedToCreatePayment: 'Failed to create payment method',
  },
  paymentMethods: {
    card: 'Card',
    paypal: 'PayPal',
    applePay: 'Apple Pay',
  },
  labels: {
    cardNumber: 'Card number',
    expiry: 'Expiration',
    cvc: 'CVC',
    country: 'Country',
    processingPayment: 'Processing payment...',
  },
  paymentType: 'card',
  defaultCountry: 'United Kingdom',
};

type Country = string;

type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'discover'
  | 'diners'
  | 'jcb'
  | 'unionpay'
  | 'unknown'
  | '';

export interface StripeWidgetRef {
  createPaymentMethod: () => Promise<PaymentMethod | null>;
  isProcessing: () => boolean;
}

interface StripeWidgetProps {
  onPaymentMethodCreated?: (paymentMethod: PaymentMethod) => void;
  onPayPalClick?: () => void;
  onTabChange?: (tabIndex: number) => void;
  className?: string;
  amount?: number;
  currency?: string;
  country?: string;
}

const StripeWidget = forwardRef<StripeWidgetRef, StripeWidgetProps>(
  (props, ref) => {
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [cardBrand, setCardBrand] = useState<CardBrand>('');
    const [error, setError] = useState<string | null>(null);

    const {countries, isLoading} = useCountries();

    const [selectedCountry, setSelectedCountry] = useState<Country>(
      PAYMENT_CONFIG.defaultCountry as Country
    );
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const stripe = useStripe();
    const elements = useElements();

    const isStripeLoading = !stripe || !elements;

    const createPaymentMethod = async () => {
      if (!stripe || !elements) {
        setError(PAYMENT_CONFIG.errors.stripeNotInitialized);
        return null;
      }

      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        setError(PAYMENT_CONFIG.errors.cardDetailsNotFound);
        return null;
      }

      setIsProcessing(true);
      try {
        const result = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumber,
        });

        if (result.error) {
          setError(result.error?.message ?? null);
          return null;
        }

        return result.paymentMethod;
      } catch {
        setError(PAYMENT_CONFIG.errors.failedToCreatePayment);
        return null;
      } finally {
        setIsProcessing(false);
      }
    };

    useImperativeHandle(ref, () => ({
      createPaymentMethod,
      isProcessing: () => isProcessing,
    }));

    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      const paymentMethod = await createPaymentMethod();
      if (paymentMethod && props.onPaymentMethodCreated) {
        props.onPaymentMethodCreated(paymentMethod);
      }
    };

    const handleTabChange = (
      _: React.SyntheticEvent | null,
      newValue: number
    ) => {
      setSelectedTab(newValue);
      setError(null);
      props.onTabChange?.(newValue);
    };

    const handleCardChange = (event: StripeElementChangeEvent) => {
      if ('brand' in event && event.brand !== cardBrand) {
        setCardBrand(event.brand as CardBrand);
      }
      setError(event.error?.message ?? null);
    };

    const handleExpressCheckoutReady = (
      event: StripeExpressCheckoutElementReadyEvent
    ) => {
      // Check if Apple Pay is available
      const hasApplePay =
        event.availablePaymentMethods &&
        Object.keys(event.availablePaymentMethods).includes('apple_pay');

      if (selectedTab === 2 && !hasApplePay) {
        setError(
          'Apple Pay is not available. Please use a different payment method.'
        );
      } else {
        setError(null);
      }
    };

    const handleExpressCheckoutConfirm = async (
      event: StripeExpressCheckoutElementConfirmEvent & {
        paymentMethod?: PaymentMethod;
      }
    ) => {
      if (props.onPaymentMethodCreated && event.paymentMethod) {
        props.onPaymentMethodCreated(event.paymentMethod);
      }
    };

    if (isLoading) {
      return <Loading />;
    }

    const countryNames = countries.map(country => country.name);

    return (
      <Box sx={mainContainerStyle}>
        {isStripeLoading ? (
          <Box sx={loadingContainerStyle}>
            <CircularProgress size={30} />
            <Typography variant="body2">Loading payment form...</Typography>
          </Box>
        ) : (
          <>
            <Box sx={paymentOptionsContainerStyle}>
              <Box
                sx={selectedPaymentOptionStyle(selectedTab === 0)}
                onClick={() => handleTabChange(null, 0)}
              >
                <FaCreditCard style={creditCardIconStyle} />
                <Typography
                  variant="body1"
                  sx={{
                    ...cardLabelStyle,
                    color: selectedTab === 0 ? '#7625DC' : '#98A2B3',
                  }}
                >
                  {PAYMENT_CONFIG.paymentMethods.card}
                </Typography>
              </Box>

              <Box
                sx={selectedPaymentOptionStyle(selectedTab === 1)}
                onClick={() => handleTabChange(null, 1)}
              >
                <PayPal />
                <Typography
                  variant="body1"
                  sx={{
                    ...paymentMethodLabelStyle,
                    color: selectedTab === 1 ? '#7625DC' : '#98A2B3',
                  }}
                >
                  {PAYMENT_CONFIG.paymentMethods.paypal}
                </Typography>
              </Box>

              <Box
                sx={selectedPaymentOptionStyle(selectedTab === 2)}
                onClick={() => handleTabChange(null, 2)}
              >
                <AppleIcon />
                <Typography
                  variant="body1"
                  sx={{
                    ...paymentMethodLabelStyle,
                    color: selectedTab === 2 ? '#7625DC' : '#98A2B3',
                  }}
                >
                  {PAYMENT_CONFIG.paymentMethods.applePay}
                </Typography>
              </Box>
            </Box>

            {selectedTab === 0 && (
              <Box sx={cardWidgetContainerStyle}>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={cardFormContainerStyle}
                >
                  {selectedTab === 0 && (
                    <>
                      <Box sx={cardFormColumnStyle}>
                        <Box sx={cardNumberRowStyle}>
                          <Box sx={cardNumberColumnStyle}>
                            <InputLabel sx={inputLabelStyle}>
                              {PAYMENT_CONFIG.labels.cardNumber}
                            </InputLabel>
                            <CardNumberElement
                              options={cardElementOptions}
                              onChange={handleCardChange}
                            />
                          </Box>

                          <Box sx={cardIconsContainerStyle}>
                            <MasterCardIcon />
                            <AmexIcon />
                            <VisaCardIcon />
                          </Box>
                        </Box>

                        <Box sx={cardDetailsRowStyle}>
                          <Box sx={cardExpiryColumnStyle}>
                            <InputLabel sx={inputLabelStyle}>
                              {PAYMENT_CONFIG.labels.expiry}
                            </InputLabel>
                            <CardExpiryElement
                              options={cardElementOptions}
                              onChange={handleCardChange}
                            />
                          </Box>

                          <Box sx={cardCvcRowStyle}>
                            <Box sx={cardCvcColumnStyle}>
                              <InputLabel sx={inputLabelStyle}>
                                {PAYMENT_CONFIG.labels.cvc}
                              </InputLabel>
                              <CardCvcElement
                                options={cardElementOptions}
                                onChange={handleCardChange}
                              />
                            </Box>
                            <Box sx={cardCvcIconContainerStyle}>
                              <FaCreditCard style={smallCreditCardIconStyle} />
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={countryColumnStyle}>
                          <InputLabel sx={inputLabelStyle}>
                            {PAYMENT_CONFIG.labels.country}
                          </InputLabel>
                          <Box sx={countrySelectRowStyle}>
                            <Typography
                              className="selected-country-label"
                              sx={selectedCountryLabelStyle}
                            >
                              {selectedCountry}
                            </Typography>
                            <Select
                              value={selectedCountry}
                              onChange={e =>
                                setSelectedCountry(e.target.value as Country)
                              }
                              fullWidth
                              sx={countrySelectOverlayStyle}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 180,
                                  },
                                },
                              }}
                            >
                              {countryNames.map(name => (
                                <MenuItem key={name} value={name}>
                                  {name}
                                </MenuItem>
                              ))}
                            </Select>
                            <KeyboardArrowDown sx={countryArrowIconStyle} />
                          </Box>
                        </Box>
                        {error && (
                          <Typography sx={errorMessageStyle}>
                            {error}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}

            {selectedTab === 1 && (
              <Box sx={cardWidgetContainerStyle}>
                <Box sx={cardFormContainerStyle}>
                  <button
                    type="button"
                    onClick={props.onPayPalClick}
                    style={{
                      padding: '10px 20px',
                      background: '#FFC43A',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                    }}
                  >
                    <PayPalLogo />
                  </button>
                </Box>
              </Box>
            )}

            {selectedTab === 2 && (
              <Box sx={cardWidgetContainerStyle}>
                <Box sx={cardFormContainerStyle}>
                  <ExpressCheckoutElement
                    onReady={handleExpressCheckoutReady}
                    onConfirm={handleExpressCheckoutConfirm}
                    options={{
                      paymentMethodOrder: ['apple_pay'],
                      paymentMethods: {
                        applePay: 'always',
                        googlePay: 'never',
                        link: 'never',
                      },
                      buttonType: {
                        applePay: 'buy',
                      },
                      layout: {
                        maxColumns: 1,
                        maxRows: 1,
                        overflow: 'auto',
                      },
                    }}
                  />
                  {error && (
                    <Typography sx={errorMessageStyle}>{error}</Typography>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }
);

export default StripeWidget;
